import clsx from 'clsx';
import type { FunctionComponent, HTMLAttributes, ReactNode } from 'react';

import { HardSkillIcon } from '../../core/icons/HardSkillIcon';
import { LanguageSkillIcon } from '../../core/icons/LanguageSkillIcon';
import { SoftSkillIcon } from '../../core/icons/SoftSkillIcon';
import { RecessedSurface } from '../../core/recessed-surface/RecessedSurface';

export type SkillBadgeProps = HTMLAttributes<HTMLDivElement> & {
    readonly $isSelected?: boolean;
    readonly actions?: ReactNode;
    readonly skill: {
        readonly title: string;
        readonly type: 'hard' | 'language' | 'soft';
    };
};

const skillBadgeBaseClasses = clsx('min-w-0 max-w-full text-body-s font-medium font-sans', 'select-none antialiased');

const skillBadgeContentClasses = clsx('flex items-center justify-center gap-s py-s px-l', '[&>svg]:shrink-0');

const skillBadgeInteractiveClasses = clsx(
    'cursor-default hover:cursor-pointer',
    'focus-visible:outline focus-visible:outline-1 focus-visible:outline-focus-ring',
);

export const SkillBadge: FunctionComponent<SkillBadgeProps> = ({
    $isSelected = false,
    actions,
    className,
    skill,
    ...props
}) => {
    const badgeContent = (
        <>
            {skill.type === 'hard' ? <HardSkillIcon /> : null}
            {skill.type === 'language' ? <LanguageSkillIcon /> : null}
            {skill.type === 'soft' ? <SoftSkillIcon /> : null}
            <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{skill.title}</span>
            {actions}
        </>
    );

    return (
        // biome-ignore lint/a11y/useSemanticElements: suppressed due to migration
        <div
            className={clsx(skillBadgeBaseClasses, skillBadgeInteractiveClasses, className)}
            role="button"
            {...props}
            data-selected={$isSelected}
            tabIndex={props.onClick !== undefined ? 0 : undefined}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    props.onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
                }
            }}
        >
            <RecessedSurface
                active={$isSelected}
                as="span"
                className="!rounded-md"
                pocketTone="orange"
                radius="md"
                renderMode="overlay"
                surfaceClassName={clsx(
                    skillBadgeContentClasses,
                    $isSelected
                        ? 'text-text-selected'
                        : 'rounded-md bg-surface-subtle text-text-primary shadow-surface-raised-subtle outline outline-1 -outline-offset-1 outline-surface-edge transition-[background-color,box-shadow] duration-200 ease-in-out hover:bg-surface-hover hover:shadow-surface-raised-subtle-hover',
                )}
                surfaceShadow="sm"
                surfaceTone="gray"
                transition="none"
            >
                {badgeContent}
            </RecessedSurface>
        </div>
    );
};
